import {useLazyQuery} from '@apollo/client';
import {useCallback} from 'react';
import {
    PodRiskReviewFilters,
    PodRiskReviewTabType,
    PodRiskReviewType,
    tabToIsFinished
} from '../graphql/types/PodRiskReviewType';
import {
    GET_POD_RISK_REVIEW_PAGE_LIST,
    GetPodRiskReviewPageListInput,
    GetPodRiskReviewPageListResponse,
    PodRiskReviewFiltersTypeInput
} from '../graphql/queries/getPodRiskReviewPageList';

export interface PodRiskReviewQueryResult {
    podRiskReviewData: PodRiskReviewType[];
    loading: boolean;
    executeQuery: (
        filters: PodRiskReviewFilters,
        page: number,
        size: number,
        tab: PodRiskReviewTabType
    ) => void;
    totalElements: number;
    error?: any;
}

/**
 * 构建筛选条件入参（列表查询与计数查询共用）
 * 全部条件为空时返回 undefined，省一次无意义的空对象传输；tab 由页面单独持有并在此合入。
 */
export const buildFiltersInputFromFilters = (
    filters: PodRiskReviewFilters,
    tab?: PodRiskReviewTabType
): PodRiskReviewFiltersTypeInput | undefined => {
    // hubIds 不参与判定：它只驱动筛选区级联，不提交后端（后端按 hubSortingCodes 筛）
    const hasAnyCondition =
        !!filters.statuses?.length ||
        !!filters.trackingNumbers ||
        !!filters.channelOrderNumbers ||
        !!filters.dspIds?.length ||
        !!filters.driverIds?.length ||
        !!filters.warehouseIds?.length ||
        !!filters.hubSortingCodes?.length ||
        !!filters.areaSortingCodes?.length ||
        !!filters.reviewerId ||
        !!filters.riskTypeCodes?.length ||
        !!filters.deliveredAt?.[0] ||
        !!filters.deliveredAt?.[1] ||
        !!filters.createdAt?.[0] ||
        !!filters.createdAt?.[1] ||
        !!filters.reviewedAt?.[0] ||
        !!filters.reviewedAt?.[1];

    if (!hasAnyCondition && !tab) {
        return undefined;
    }

    return {
        isFinished: tabToIsFinished(tab),
        statuses: filters.statuses?.length ? filters.statuses : undefined,
        trackingNumbers: filters.trackingNumbers || undefined,
        channelOrderNumbers: filters.channelOrderNumbers || undefined,
        dspIds: filters.dspIds?.length ? filters.dspIds : undefined,
        driverIds: filters.driverIds?.length ? filters.driverIds : undefined,
        warehouseIds: filters.warehouseIds?.length ? filters.warehouseIds : undefined,
        hubSortingCodes: filters.hubSortingCodes?.length ? filters.hubSortingCodes : undefined,
        areaSortingCodes: filters.areaSortingCodes?.length ? filters.areaSortingCodes : undefined,
        reviewerId: filters.reviewerId || undefined,
        riskTypeCodes: filters.riskTypeCodes?.length ? filters.riskTypeCodes : undefined,
        // 后端为 `>= from` / `< to`（不含）；控件只选到日期，故规整到当日首尾时刻
        deliveredAtFrom: filters.deliveredAt?.[0]?.startOf('day').toISOString() ?? undefined,
        deliveredAtTo: filters.deliveredAt?.[1]?.endOf('day').toISOString() ?? undefined,
        createdAtFrom: filters.createdAt?.[0]?.startOf('day').toISOString() ?? undefined,
        createdAtTo: filters.createdAt?.[1]?.endOf('day').toISOString() ?? undefined,
        reviewedAtFrom: filters.reviewedAt?.[0]?.startOf('day').toISOString() ?? undefined,
        reviewedAtTo: filters.reviewedAt?.[1]?.endOf('day').toISOString() ?? undefined
    };
};

const buildQueryVariables = (
    filters: PodRiskReviewFilters,
    page: number,
    size: number,
    tab: PodRiskReviewTabType
): GetPodRiskReviewPageListInput => ({
    filters: buildFiltersInputFromFilters(filters, tab),
    page,
    size
});

export const usePodRiskReviewQuery = (): PodRiskReviewQueryResult => {
    const [execute, {data, loading, error}] = useLazyQuery<GetPodRiskReviewPageListResponse>(
        GET_POD_RISK_REVIEW_PAGE_LIST,
        {
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const executeQuery = useCallback(
        (
            filters: PodRiskReviewFilters,
            page: number,
            size: number,
            tab: PodRiskReviewTabType
        ) => {
            execute({variables: buildQueryVariables(filters, page, size, tab)});
        },
        [execute]
    );

    return {
        podRiskReviewData: data?.getPodRiskReviewPageList?.content || [],
        loading,
        executeQuery,
        totalElements: data?.getPodRiskReviewPageList?.totalElements || 0,
        error
    };
};
